import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { exchangeCode } from '@/shared/auth/cognito';
import { useAuthStore } from '@/shared/auth/useAuthStore';
import { useNotificationStore } from '@/shared/api/notificationStore';
import { axiosClient } from '@/shared/api/axiosClient';
import { routePaths } from '@/app/router/routePaths';
import type { User } from '@/entities/user/model/user.types';
import type { ApiSuccess } from '@/shared/api/apiResponse.types';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { setTokens, setUser, clear } = useAuthStore();
  const notify = useNotificationStore((s) => s.show);
  const handled = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-invoke
    if (handled.current) return;
    handled.current = true;

    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) {
      notify('Sign-in was cancelled or failed. Please try again.', 'error');
      navigate(routePaths.landing, { replace: true });
      return;
    }

    (async () => {
      try {
        const tokens = await exchangeCode(code);
        setTokens({
          idToken: tokens.id_token,
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          expiresIn: tokens.expires_in,
          client: 'dj',
        });

        // GET /me creates the user record on first login
        const res = await axiosClient.get<ApiSuccess<User>>('/me', {
          headers: { Authorization: `Bearer ${tokens.id_token}` },
        });
        setUser(res.data.data);

        navigate(routePaths.djDashboard, { replace: true });
      } catch (err) {
        console.error('Auth callback failed', err);
        clear();
        notify('Sign-in failed. Please try again.', 'error');
        navigate(routePaths.landing, { replace: true });
      }
    })();
  }, [navigate, setTokens, setUser, clear, notify]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, bgcolor: 'background.default' }}>
      <CircularProgress />
      <Typography color="text.secondary">Signing you in…</Typography>
    </Box>
  );
}
